/// <amd-module name="Wit/Common/WorkItemLinksService/WorkItemLinksService" />
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { allLinksFilter, LinkTypeFilter } from "Wit/Common/Utils/WorkItemUtils/LinkTypeFilter";
import { IRegisteredLinkType, IWorkItemLinkType, IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";
import { LinkTypeFieldIds, RegisteredLinkTypeNames, ToolIds } from "Wit/WorkItemLinksRegistration/Constants";
import { ILinkIdentifier, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { getLinkTypeDisplayName, isSameLinkIdentifier } from "Wit/WorkItemLinksRegistration/LinkUtils";
import { observeArtifactResolution } from "Wit/WorkItemLinksRegistration/resolveArtifacts";
import { ILink, IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { Attachment } from "Wit/WorkItemModel/WorkItem/Links";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { IBacklogConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { BaseStringComparer, CompositeComparer, SortDirection, StateComparer, BaseIntComparer, sortByNumberValue } from "./CompositeComparer";
import { LinkTypeName } from "Wit/WorkItemModel/OM/WorkItemConstants";

export interface IWorkItemLinksService extends IVssService {
    /**
     * Returns an observable aray of resolve link data for the provided work item and options.
     * If this data is not already present, it will create a new array which will be populated
     * as the data loads.
     */
    getObservableLinkData(workItem: IWorkItem, options: IGetLinkDataOptions): IObservableArray<IResolvedLinkDisplayData>;

    /**
     * Returns an observable object containing the total number of links matching the current filter, and the number that are included.
     */
    getObservableLinkCounts(workItem: IWorkItem, options: IGetLinkDataOptions): IObservableValue<ILinkCounts>;

    /**
     * Causes the link data for the provided work item to be reloaded.
     */
    refreshLinkData(workItem: IWorkItem, options?: IGetLinkDataOptions): void;
    getLinkIdentifiers(workItem: IWorkItem, options: IGetLinkDataOptions): Promise<ILinkIdentifier[]>;
    getLinkTypes(): Promise<IRegisteredLinkType[]>;
    getWitLinkTypes(): Promise<IWorkItemLinkType[]>;
    getWitLinkType(refName: string): Promise<IWorkItemLinkType | undefined>;
    getWitLinkTypeEnd(idOrImmutableName: number | string | undefined): Promise<IWorkItemLinkTypeEnd | undefined>;
    getWitLinkTypeEnds(): Promise<IWorkItemLinkTypeEnd[]>;

    /**
     * Compares link types. Using this in Array.prototype.sort will result in high-priority link
     * types first, followed by other link types in alphabetical order by display name.
     */
    makeLinkComparer<T>(linkTypeExtractor: (item: T) => string): CompositeComparer<T>;
}

interface IGetLinkDataOptions {
    linkTypeFilter?: LinkTypeFilter;
    includeDeleted?: boolean;
    maxLinkCount?: number;
    ignoreArtifactCache?: boolean;
    zeroDataExperience?: string | undefined;
}

export interface ILinkCounts {
    shown: number;
    total: number;
    notShown: { [linkType: string]: number };
}

interface ILinkDataState {
    observableDisplayData: IObservableArray<IResolvedLinkDisplayData>;
    hiddenLinkCounts: IObservableValue<ILinkCounts>;
}

/**
 * The keys are, in order work item id, link type filter, maxLinkCount, and includeDeleted
 */
type ResolvedLinkDataStore = Map<number, Map<LinkTypeFilter, Map<number, Map<boolean, ILinkDataState>>>>;

/**
 * These link types should be loaded first and appear first in some controls, in this order.
 */
const prioritizedLinkTypes = [
    LinkTypeName.Parent_Link_Name, // Parent
    LinkTypeName.Child_Link_Name, // Child
    LinkTypeName.Predecessor_Link_Name, // Predecessor
    LinkTypeName.Successor_Link_Name, // Successor
    LinkTypeName.Duplicate_Link_Name, // Duplicate
    LinkTypeName.DuplicateOf_Link_Name, // Duplicate of
    LinkTypeName.Related_Link_Name // Related
];

const artifactLinkTypes = [
    RegisteredLinkTypeNames.Build,
    RegisteredLinkTypeNames.IntegratedInBuild,
    RegisteredLinkTypeNames.PullRequest,
    RegisteredLinkTypeNames.Branch,
    RegisteredLinkTypeNames.Commit,
    RegisteredLinkTypeNames.Changeset,
    RegisteredLinkTypeNames.VersionedItem,
    RegisteredLinkTypeNames.FoundInBuild,
    RegisteredLinkTypeNames.GitHubPullRequestLinkType,
    RegisteredLinkTypeNames.GitHubCommitLinkType
] as Array<String>;

export class WorkItemLinksService extends VssService implements IWorkItemLinksService {
    private workItemDataManager: IWorkItemDataManager;
    private initializationPromise: Promise<void>;
    private registeredLinkTypes = new Map<string, IRegisteredLinkType>();
    private witLinkTypes = new Map<string, IWorkItemLinkType>();
    private witLinkTypeEnds = new Map<string | number, IWorkItemLinkTypeEnd>();
    private resolvedLinkDataObservables: ResolvedLinkDataStore = new Map();
    private backlogConfiguration: IBacklogConfiguration | undefined;
    private workItemTypesService: IWorkItemTypesService;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this.workItemDataManager = pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        this.workItemTypesService = pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");

        const commonBacklogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.backlogConfiguration = commonBacklogContentService.getBacklogConfigurationSync();

        this.initializationPromise = this.workItemDataManager.beginGetLinkTypes().then(linksMetadata => {
            if (!linksMetadata) {
                throw new Error("Links data not found");
            }

            for (const registeredLinkType of linksMetadata.registeredLinkTypes) {
                this.registeredLinkTypes.set(registeredLinkType.name, registeredLinkType);
            }
            for (const workItemLinkType of linksMetadata.witLinkTypes) {
                this.witLinkTypes.set(workItemLinkType.referenceName, workItemLinkType);

                // Set references back from the link type ends to the link type.
                // This creates circular references so these are no longer serializable,
                // but it saves us from passing data around
                workItemLinkType.forwardEnd.linkType = workItemLinkType;
                workItemLinkType.reverseEnd.linkType = workItemLinkType;
                this.witLinkTypeEnds.set(workItemLinkType.forwardEnd.id, workItemLinkType.forwardEnd);
                this.witLinkTypeEnds.set(workItemLinkType.forwardEnd.immutableName, workItemLinkType.forwardEnd);
                this.witLinkTypeEnds.set(workItemLinkType.reverseEnd.id, workItemLinkType.reverseEnd);
                this.witLinkTypeEnds.set(workItemLinkType.reverseEnd.immutableName, workItemLinkType.reverseEnd);
            }
        });
    }

    public getObservableLinkData(workItem: IWorkItem, options: IGetLinkDataOptions): IObservableArray<IResolvedLinkDisplayData> {
        return this.getLinkDataState(workItem, options).observableDisplayData;
    }

    public getObservableLinkCounts(workItem: IWorkItem, options: IGetLinkDataOptions): IObservableValue<ILinkCounts> {
        return this.getLinkDataState(workItem, options).hiddenLinkCounts;
    }

    public refreshLinkData(workItem: IWorkItem, options?: IGetLinkDataOptions): void {
        if (options) {
            // Refresh link data for the given set of options
            this.refreshLinkDataForOptions(workItem, options);
        } else {
            // Refresh all link data for the work item
            for (const options of this.getCachedLinkDataOptions(workItem)) {
                this.refreshLinkDataForOptions(workItem, options);
            }
        }
    }

    private refreshLinkDataForOptions(workItem: IWorkItem, options: IGetLinkDataOptions): void {
        const linkData = this.getExistingLinkData(workItem, options) ?? createInitialLinkData();
        this.setExistingLinkData(workItem, options, linkData);

        Promise.all([
            this.getLinkIdentifiers(workItem, options),
            this.getLinkIdentifiers(workItem, { ...options, maxLinkCount: Number.MAX_SAFE_INTEGER })
        ]).then(([identifiersToDisplay, allMatchingIdentifiers]) => {
            const hiddenIdentifiers = allMatchingIdentifiers.filter(
                identifier => !identifiersToDisplay.find(shownIdentifier => isSameLinkIdentifier(identifier, shownIdentifier))
            );

            const notShown: { [linkType: string]: number } = {};

            for (const hiddenIdentifier of hiddenIdentifiers) {
                const displayName = this.getLinkTypeName(hiddenIdentifier);
                const count = notShown[displayName] ?? 0;
                notShown[displayName] = count + 1;
            }

            linkData.hiddenLinkCounts.value = {
                total: allMatchingIdentifiers.length,
                shown: identifiersToDisplay.length,
                notShown
            };

            let compositeComparer = new CompositeComparer<IResolvedLinkDisplayData>();

            if (options.zeroDataExperience === "Development") {
                //apply sorting by date or state only for Development section Links

                compositeComparer.push(
                    new StateComparer(
                        SortDirection.Ascending,
                        this.backlogConfiguration,
                        this.workItemTypesService,
                        //apply sorting by date only to PRs
                        (a, b) =>
                            a?.linkTypeDisplayName === RegisteredLinkTypeNames.PullRequest ||
                            b?.linkTypeDisplayName === RegisteredLinkTypeNames.PullRequest
                    )
                );

                compositeComparer.push(new BaseIntComparer(SortDirection.Descending, item => item?.updateDate?.getTime() ?? 0));
            }

            compositeComparer.push(new BaseStringComparer(SortDirection.Ascending, item => item?.title));
            compositeComparer.push(new BaseStringComparer(SortDirection.Ascending, item => item?.id.toString()));

            observeArtifactResolution(
                identifiersToDisplay,
                workItem.id,
                this.pageContext,
                linkData.observableDisplayData,
                options.ignoreArtifactCache,
                compositeComparer.getComparer(),
                this.witLinkTypeEnds
            );
        });
    }

    private getLinkDataState(workItem: IWorkItem, options: IGetLinkDataOptions): ILinkDataState {
        const existingLinkData = this.getExistingLinkData(workItem, options);
        if (existingLinkData) {
            return existingLinkData;
        }

        this.refreshLinkData(workItem, options);
        return this.getExistingLinkData(workItem, options)!;
    }

    private makeLinkComparerInternal(): CompositeComparer<ILink> {
        const compositeComparer = this.makeLinkComparer<ILink>(link => {
            const linkTypeEnd = this.getWitLinkTypeEndSync(link.linkData.LinkType);
            const valueToCompare = linkTypeEnd?.immutableName ?? link.linkData.OriginalName ?? RegisteredLinkTypeNames.Related;
            return valueToCompare;
        });

        // If link type is equals compare by link update date
        compositeComparer.push(
            new BaseIntComparer(SortDirection.Descending, valueSource => {
                const valueToCompare = valueSource?.linkData?.LastWriteDate?.getTime() ?? valueSource?.linkData["Changed Date"]?.getTime() ?? 0;
                return valueToCompare;
            })
        );

        return compositeComparer;
    }

    public async getLinkIdentifiers(
        workItem: IWorkItem,
        { linkTypeFilter, includeDeleted, maxLinkCount }: IGetLinkDataOptions
    ): Promise<ILinkIdentifier[]> {
        await this.initializationPromise;

        const compositeComparer = this.makeLinkComparerInternal();

        let count = 0;
        return workItem
            .getLinks(includeDeleted)
            .sort(compositeComparer.getComparer())
            .filter(link => {
                if (link instanceof Attachment) {
                    return false;
                }

                if (!linkTypeFilter) {
                    return true;
                }

                const linkTypeEndId = link.getWitLinkTypeEndId();
                const linkTypeEndImmutableName = linkTypeEndId === undefined ? undefined : this.getWitLinkTypeEndSync(linkTypeEndId)?.immutableName;
                if (!linkTypeFilter(link.getLinkType(), linkTypeEndImmutableName)) {
                    return false;
                }

                if (++count > (maxLinkCount ?? Number.MAX_SAFE_INTEGER)) {
                    return false;
                }

                return true;
            })
            .map(link => ({
                ...link.linkData,
                FldID: link.linkData.FldID || LinkTypeFieldIds.WorkItem,
                tool: this.getLinkTypeToolId(link)
            }));
    }

    public makeLinkComparer<T>(linkTypeExtractor: (item: T) => string): CompositeComparer<T> {
        const compositeComparer = new CompositeComparer<T>();

        // First sort by parent - child
        compositeComparer.push(
            new BaseIntComparer(SortDirection.Ascending, valueSource => {
                const prioritizedIndex = prioritizedLinkTypes.indexOf(linkTypeExtractor(valueSource));
                // If link type is not prioritized, put it at the end of the list and
                // After that, just list out the rest alphabetically
                if (prioritizedIndex < 0) {
                    return prioritizedLinkTypes.length + 1;
                }

                return prioritizedIndex;
            })
        );

        // If neither link type is prioritized, sort by artifact link type
        compositeComparer.push(
            new BaseIntComparer(SortDirection.Ascending, valueSource => {
                const artifactIndex = artifactLinkTypes.indexOf(linkTypeExtractor(valueSource));
                return artifactIndex;
            })
        );

        // If neither link type is prioritized or artifact, sort by link type name
        compositeComparer.push(
            new BaseStringComparer(SortDirection.Ascending, valueSource => {
                const valueToCompareLinkTypeEnd = this.getWitLinkTypeEndSync(linkTypeExtractor(valueSource));
                const valueToCompareLinkTypeName = valueToCompareLinkTypeEnd?.name ?? getLinkTypeDisplayName(linkTypeExtractor(valueSource));
                return valueToCompareLinkTypeName;
            })
        );

        return compositeComparer;
    }

    public getAttachments(workItem: IWorkItem): Attachment[] {
        return workItem.getLinks().filter(l => l instanceof Attachment && !l.isRemoved()) as Attachment[];
    }

    public async getLinkTypes(): Promise<IRegisteredLinkType[]> {
        await this.initializationPromise;
        return Array.from(this.registeredLinkTypes.values());
    }

    public async getWitLinkTypes(): Promise<IWorkItemLinkType[]> {
        await this.initializationPromise;
        return Array.from(this.witLinkTypes.values());
    }

    public async getWitLinkType(refName: string): Promise<IWorkItemLinkType | undefined> {
        await this.initializationPromise;
        return this.witLinkTypes.get(refName);
    }

    public async getWitLinkTypeEnd(idOrImmutableName: number | string | undefined): Promise<IWorkItemLinkTypeEnd | undefined> {
        await this.initializationPromise;
        return this.getWitLinkTypeEndSync(idOrImmutableName);
    }

    public async getWitLinkTypeEnds(): Promise<IWorkItemLinkTypeEnd[]> {
        await this.initializationPromise;
        return Array.from(this.witLinkTypeEnds.values());
    }

    private getCachedLinkDataOptions(workItem: IWorkItem): IGetLinkDataOptions[] {
        const options: IGetLinkDataOptions[] = [];

        const byFilter = this.resolvedLinkDataObservables.get(workItem.id);
        if (!byFilter) {
            return options;
        }

        for (const [linkTypeFilter, byMaxLinkCount] of byFilter.entries()) {
            for (const [maxLinkCount, byIncludeDeleted] of byMaxLinkCount.entries()) {
                for (const includeDeleted of byIncludeDeleted.keys()) {
                    options.push({ linkTypeFilter, maxLinkCount, includeDeleted });
                }
            }
        }

        return options;
    }

    /**
     * Returns the existing observable array of resolved link data for the provided work item and
     * options if it exists, and undefined otherwise.
     */
    private getExistingLinkData(
        workItem: IWorkItem,
        { linkTypeFilter, includeDeleted, maxLinkCount }: IGetLinkDataOptions
    ): ILinkDataState | undefined {
        return this.resolvedLinkDataObservables
            .get(workItem.id)
            ?.get(linkTypeFilter ?? allLinksFilter)
            ?.get(maxLinkCount ?? Number.MAX_SAFE_INTEGER)
            ?.get(includeDeleted ?? false);
    }

    /**
     * Stores a new observable array of resolved link data for the provided work item and options
     */
    private setExistingLinkData(
        workItem: IWorkItem,
        { linkTypeFilter, includeDeleted, maxLinkCount }: IGetLinkDataOptions,
        linkData: ILinkDataState
    ): void {
        if (!this.resolvedLinkDataObservables.has(workItem.id)) {
            this.resolvedLinkDataObservables.set(workItem.id, new Map());
        }

        const byFilter = this.resolvedLinkDataObservables.get(workItem.id)!;
        if (!byFilter.has(linkTypeFilter ?? allLinksFilter)) {
            byFilter.set(linkTypeFilter ?? allLinksFilter, new Map());
        }

        const byMaxLinkCount = byFilter.get(linkTypeFilter ?? allLinksFilter)!;
        if (!byMaxLinkCount.has(maxLinkCount ?? Number.MAX_SAFE_INTEGER)) {
            byMaxLinkCount.set(maxLinkCount ?? Number.MAX_SAFE_INTEGER, new Map());
        }

        const byIncludeDeleted = byMaxLinkCount.get(maxLinkCount ?? Number.MAX_SAFE_INTEGER)!;

        byIncludeDeleted.set(includeDeleted ?? false, linkData);
    }

    private getWitLinkTypeEndSync(idOrImmutableName: number | string | undefined): IWorkItemLinkTypeEnd | undefined {
        if (idOrImmutableName === undefined) {
            return undefined;
        }
        return this.witLinkTypeEnds.get(idOrImmutableName);
    }

    private getLinkTypeToolId(link: ILink): string {
        if (link.isRemote()) {
            return ToolIds.RemoteWorkItemTracking;
        }

        const linkType = link.getLinkType();
        if (!this.registeredLinkTypes.has(linkType)) {
            return ToolIds.WorkItemTracking;
        }

        return this.registeredLinkTypes.get(linkType)!.toolId;
    }

    private getLinkTypeName(linkIdentifier: ILinkIdentifier): string {
        if (linkIdentifier.LinkType) {
            return this.getWitLinkTypeEndSync(linkIdentifier.LinkType)?.name ?? getLinkTypeDisplayName(RegisteredLinkTypeNames.Related);
        }
        return getLinkTypeDisplayName(linkIdentifier.OriginalName ?? RegisteredLinkTypeNames.Related);
    }
}

/**
 * There is a brief period (usually one tick) when we start loading link data before we know the
 * correct number of placeholders to show. Start off with five placeholders during that period.
 */
function createInitialLinkData(): ILinkDataState {
    return {
        observableDisplayData: new ObservableArray(new Array(5).fill(undefined)),
        hiddenLinkCounts: new ObservableValue({ shown: 0, total: 0, notShown: {} })
    };
}

const serviceName = "IWorkItemLinksService";
Services.add(serviceName, { serviceFactory: WorkItemLinksService });
